import React from 'react';
import { Link } from 'react-router-dom';
import { Activity, Gauge, HeartPulse, Sparkles, ArrowRight, CheckCircle2, Leaf, UserRound, ClipboardList, ShieldCheck, Heart } from 'lucide-react';
import { siteData } from '../data/mock';
import SEO from '../components/SEO';
import FAQ from '../components/FAQ';
import { homeFaq } from '../data/faqContent';
import RelatedPages from '../components/RelatedPages';
import { relatedPagesMap } from '../data/relatedPages';

const ONEDOC_URL = "https://www.onedoc.ch/fr/cabinet-de-groupe/vevey/ebdj4/nutri-centre-riviera";
const ONEDOC_ZARA = "https://www.onedoc.ch/fr/therapeute-en-nutrition-mco/vevey/pcyv4/zara-nawid";

const iconMap = {
  'activity': Activity,
  'gauge': Gauge,
  'heart-pulse': HeartPulse,
  'sparkles': Sparkles
};

const Home = () => {
  return (
    <>
      <SEO 
        title="Nutritionniste Vevey, Clarens & Montreux | SOPK, Ménopause, Perte de poids — Harmonie Féminine"
        description="Nutritionniste agréée ASCA & RME à Vevey, Clarens et Montreux. Accompagnement personnalisé : SOPK, nutrition hormonale, perte de poids, périménopause, ménopause, fertilité et rééquilibrage alimentaire. Riviera vaudoise."
        keywords="nutritionniste Vevey, nutritionniste Montreux, nutritionniste Clarens, nutritionniste Riviera, SOPK, perte de poids, ménopause, périménopause, nutrition hormonale, nutritionniste ASCA RME, résistance insuline, rééquilibrage alimentaire, harmonie féminine, Nutri Centre Riviera"
        url="https://nutricentreriviera.ch"
      />
      <div style={{ marginTop: '100px' }}>
      {/* Hero Section — Image en arrière-plan plein écran */}
      <section
        className="hero-section"
        style={{
          position: 'relative',
          minHeight: 'min(82vh, 760px)',
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
          isolation: 'isolate'
        }}
        data-testid="hero-section"
      >
        {/* Image de fond */}
        <img
          src={siteData.images.hero}
          alt="Cabinet de consultation Nutri Centre Riviera — bureau lumineux avec vue panoramique sur les Alpes, Vevey"
          fetchPriority="high"
          decoding="async"
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center',
            zIndex: -2
          }}
        />

        {/* Overlay dégradé pour lisibilité (clair, élégant, mode médical) */}
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(95deg, rgba(253,252,249,0.97) 0%, rgba(253,252,249,0.9) 38%, rgba(253,252,249,0.42) 62%, rgba(253,252,249,0.12) 100%)',
            zIndex: -1
          }}
          className="hero-overlay"
        />

        <div
          className="container hero-content"
          style={{
            padding: '5rem 2rem',
            position: 'relative',
            zIndex: 1,
            width: '100%'
          }}
        >
          <div style={{ maxWidth: '640px' }}>
            <div style={{ marginBottom: '2rem' }}>
              <div
                style={{
                  color: 'var(--accent-warm)',
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  fontWeight: 700,
                  fontSize: '0.85rem',
                  marginBottom: '0.65rem'
                }}
              >
                par Nutri Centre Riviera
              </div>
              <div
                style={{
                  fontFamily: '"Playfair Display", Georgia, serif',
                  color: 'var(--text-primary)',
                  fontWeight: 700,
                  fontSize: 'clamp(2.6rem, 5.5vw, 3.85rem)',
                  lineHeight: 1.05,
                  marginBottom: '0.5rem',
                  letterSpacing: '-0.015em'
                }}
              >
                Harmonie Féminine
              </div>
              <div
                style={{
                  fontFamily: '"Playfair Display", Georgia, serif',
                  fontStyle: 'italic',
                  color: 'var(--text-muted)',
                  fontSize: '1.15rem',
                  letterSpacing: '0.01em'
                }}
              >
                Centre de nutrition &amp; santé corps–cœur
              </div>
            </div>

            <h1
              className="heading-hero"
              style={{
                marginBottom: '1.25rem',
                fontSize: 'clamp(1.8rem, 3.5vw, 2.6rem)'
              }}
            >
              Retrouvez votre équilibre alimentaire
            </h1>
            <p
              className="heading-subtitle"
              style={{ marginBottom: '0.75rem', lineHeight: 1.5 }}
            >
              Perte de poids, SOPK, santé hormonale, énergie et bien-être.
            </p>
            <p
              className="body-large"
              style={{
                marginBottom: '2.5rem',
                color: 'var(--text-muted)',
                lineHeight: 1.6
              }}
            >
              Accompagnement personnalisé à Vevey et Montreux.
            </p>

            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <a
                href={ONEDOC_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
                style={{ textDecoration: 'none' }}
                data-testid="hero-cta-rdv"
              >
                Prendre rendez-vous
                <ArrowRight size={20} />
              </a>
              <Link to="/le-centre" className="btn-secondary">
                Découvrir l'approche
              </Link>
            </div>

            <p
              className="caption"
              style={{
                marginTop: '1.25rem',
                color: 'var(--text-muted)',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <CheckCircle2 size={16} />
              {siteData.cta.subtext}
            </p>
          </div>
        </div>

        {/* Responsive */}
        <style>{`
          @media (max-width: 768px) {
            .hero-section {
              min-height: 70vh !important;
            }
            .hero-overlay {
              background: linear-gradient(180deg, rgba(253,252,249,0.95) 0%, rgba(253,252,249,0.8) 65%, rgba(253,252,249,0.55) 100%) !important;
            }
            .hero-content {
              padding: 4rem 1.25rem !important;
            }
          }
        `}</style>
      </section>

      {/* Nos prestations — 3 services principaux */}
      <section
        data-testid="services-section"
        style={{ background: 'white', padding: '6rem 0' }}
      >
        <div className="container">
          <div style={{ textAlign: 'center', maxWidth: '780px', margin: '0 auto 3.5rem' }}>
            <span className="caption" style={{
              color: 'var(--accent-warm)',
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              fontWeight: 600,
              fontSize: '0.85rem',
              display: 'inline-block',
              marginBottom: '1rem'
            }}>
              Nos prestations
            </span>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Un accompagnement à chaque étape
            </h2>
            <p className="body-large" style={{ color: 'var(--text-muted)', lineHeight: 1.7 }}>
              Quatre prestations clés, pensées spécifiquement pour la santé féminine.
            </p>
          </div>

          <div
            className="services-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: '1.75rem',
              maxWidth: '1280px',
              margin: '0 auto'
            }}
          >
            {[
              {
                icon: Leaf,
                title: "Consultation nutritionnelle",
                price: "150",
                description: "Accompagnement nutritionnel personnalisé pour la perte de poids, les troubles digestifs, le diabète, le cholestérol, la fatigue et le rééquilibrage alimentaire.",
                href: "/prestations/consultation-nutritionnelle",
                testid: "service-consultation"
              },
              {
                icon: UserRound,
                title: "Santé de la femme",
                price: "150",
                description: "Nutrition adaptée aux différentes étapes de la vie de la femme : équilibre hormonal, SOPK, fertilité, grossesse, post-partum et ménopause.",
                href: "/prestations/consultation-nutritionnelle",
                testid: "service-sante-femme"
              },
              {
                icon: Activity,
                title: "Analyse corporelle & micronutrition",
                price: "Dès 150",
                description: "Analyse complète de la composition corporelle et bilan micronutritionnel permettant d'identifier les déséquilibres, d'optimiser votre santé et de personnaliser davantage votre accompagnement.",
                href: "/prestations/consultation-nutritionnelle",
                testid: "service-analyse-micronutrition"
              },
              {
                icon: Sparkles,
                title: "Séance d'hypnopraxie",
                price: "150",
                badge: "Nouveau",
                description: "Retrouvez vos ressources intérieures et avancez vers vos objectifs avec plus de sérénité. L'hypnopraxie permet de travailler sur :",
                bullets: [
                  "le stress et les émotions",
                  "les blocages personnels",
                  "les compulsions alimentaires",
                  "la confiance en soi",
                  "les changements de vie"
                ],
                footnote: "Les séances peuvent être réalisées indépendamment d'un suivi nutritionnel.",
                href: "/prestations/hypnopraxie",
                testid: "service-hypnopraxie"
              }
            ].map((svc, idx) => {
              const Icon = svc.icon;
              return (
                <article
                  key={idx}
                  className="service-card"
                  data-testid={svc.testid}
                  style={{
                    background: 'white',
                    border: '1px solid var(--border-light)',
                    borderRadius: '1.5rem',
                    padding: '2.5rem 1.75rem 2rem',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 4px 24px rgba(0,0,0,0.04)',
                    position: 'relative'
                  }}
                >
                  {/* Badge "Nouveau" */}
                  {svc.badge && (
                    <span
                      data-testid={`${svc.testid}-badge`}
                      style={{
                        position: 'absolute',
                        top: '1rem',
                        right: '1rem',
                        background: 'var(--accent-gold)',
                        color: '#1C2321',
                        padding: '0.3rem 0.75rem',
                        borderRadius: '999px',
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        boxShadow: '0 4px 12px rgba(61, 84, 70, 0.25)'
                      }}
                    >
                      {svc.badge}
                    </span>
                  )}
                  {/* Icon */}
                  <div style={{
                    width: '72px',
                    height: '72px',
                    borderRadius: '50%',
                    background: 'var(--accent-primary-light, #E9EEE8)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '1.75rem'
                  }}>
                    <Icon size={32} strokeWidth={1.5} style={{ color: 'var(--accent-primary)' }} />
                  </div>

                  {/* Titre */}
                  <h3 className="heading-3" style={{
                    marginBottom: '1rem',
                    color: 'var(--text-primary)',
                    fontSize: '1.35rem',
                    marginTop: '0'
                  }}>
                    {svc.title}
                  </h3>

                  {/* Description */}
                  <p className="body-medium" style={{
                    color: 'var(--text-muted)',
                    lineHeight: 1.65,
                    marginBottom: svc.bullets ? '0.85rem' : '2rem',
                    flex: svc.bullets ? '0 0 auto' : 1
                  }}>
                    {svc.description}
                  </p>

                  {/* Bullets (optionnel) */}
                  {svc.bullets && (
                    <ul style={{
                      listStyle: 'none',
                      padding: 0,
                      margin: '0 0 1rem 0',
                      width: '100%',
                      textAlign: 'left',
                      flex: 1
                    }}>
                      {svc.bullets.map((b, i) => (
                        <li
                          key={i}
                          style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '0.6rem',
                            fontSize: '0.92rem',
                            color: 'var(--text-muted)',
                            lineHeight: 1.55,
                            marginBottom: '0.4rem'
                          }}
                        >
                          <span style={{
                            width: '6px',
                            height: '6px',
                            borderRadius: '50%',
                            background: 'var(--accent-primary)',
                            flexShrink: 0,
                            marginTop: '0.55rem'
                          }} />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* Footnote (optionnel) */}
                  {svc.footnote && (
                    <p style={{
                      fontSize: '0.82rem',
                      color: 'var(--text-muted)',
                      fontStyle: 'italic',
                      margin: '0 0 1.5rem 0',
                      lineHeight: 1.55
                    }}>
                      {svc.footnote}
                    </p>
                  )}

                  {/* CTA */}
                  <Link
                    to={svc.href}
                    className="btn-primary"
                    style={{
                      textDecoration: 'none',
                      letterSpacing: '0.04em'
                    }}
                    data-testid={`${svc.testid}-cta`}
                  >
                    En savoir +
                    <ArrowRight size={18} />
                  </Link>

                  {/* Note remboursement — uniquement si prix défini (tarifs cachés sur home) */}
                  {svc.price && (
                    <div style={{
                      marginTop: '1.5rem',
                      textAlign: 'center'
                    }}>
                      <p style={{
                        fontSize: '0.82rem',
                        color: 'var(--text-muted)',
                        fontStyle: 'italic',
                        margin: 0,
                        lineHeight: 1.4
                      }}>
                        Remboursé par l'ASCA et le RME
                      </p>
                    </div>
                  )}
                </article>
              );
            })}
          </div>

          <style>{`
            .service-card:hover {
              transform: translateY(-6px);
              border-color: var(--accent-primary);
              box-shadow: 0 14px 40px rgba(61, 84, 70, 0.14);
            }
            @media (max-width: 1100px) {
              .services-grid {
                grid-template-columns: repeat(2, 1fr) !important;
                max-width: 800px !important;
              }
            }
            @media (max-width: 600px) {
              .services-grid {
                grid-template-columns: 1fr !important;
                max-width: 480px !important;
              }
            }
          `}</style>
        </div>
      </section>

      {/* Nos spécialisations */}
      <section
        data-testid="specializations-section"
        style={{ background: 'var(--bg-section)', padding: '6rem 0' }}
      >
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '3rem', maxWidth: '820px', margin: '0 auto 3rem' }}>
            <span className="caption" style={{
              color: 'var(--accent-warm)',
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              fontWeight: 600,
              fontSize: '0.85rem',
              display: 'inline-block',
              marginBottom: '1rem'
            }}>
              Nos spécialisations
            </span>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Une vraie expertise féminine et métabolique
            </h2>
            <p className="body-large" style={{ color: 'var(--text-muted)', lineHeight: 1.7 }}>
              Nous accompagnons les femmes sur l'ensemble des problématiques qui touchent leur corps, leurs hormones et leur équilibre émotionnel — à Vevey, Clarens et Montreux.
            </p>
          </div>

          <div
            className="specializations-chips"
            data-testid="specializations-chips"
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: '0.75rem',
              maxWidth: '950px',
              margin: '0 auto'
            }}
          >
            {[
              "SOPK & déséquilibres hormonaux",
              "Perte de poids durable",
              "Métabolisme féminin",
              "Fatigue chronique",
              "Compulsions & émotions",
              "Rétention d'eau & circulation",
              "Préménopause & ménopause",
              "Comportement alimentaire",
              "Prévention cardio-métabolique"
            ].map((label, idx) => (
              <span
                key={idx}
                className="specialization-chip"
                data-testid={`specialization-chip-${idx}`}
                style={{
                  background: 'white',
                  border: '1px solid var(--border-light)',
                  borderRadius: '999px',
                  padding: '0.75rem 1.4rem',
                  fontSize: '0.95rem',
                  color: 'var(--text-primary)',
                  fontWeight: 500,
                  transition: 'all 0.25s ease',
                  cursor: 'default',
                  whiteSpace: 'nowrap'
                }}
              >
                {label}
              </span>
            ))}
          </div>

          <style>{`
            .specialization-chip:hover {
              border-color: var(--accent-primary);
              color: var(--accent-primary);
              transform: translateY(-2px);
              box-shadow: 0 4px 14px rgba(61, 84, 70, 0.12);
            }
            @media (max-width: 600px) {
              .specialization-chip {
                white-space: normal !important;
                font-size: 0.88rem !important;
                padding: 0.6rem 1.1rem !important;
              }
            }
          `}</style>
        </div>
      </section>

      {/* Vous vous reconnaissez si... */}
      <section
        data-testid="recognition-section"
        style={{ background: 'white', padding: '6rem 0' }}
      >
        <div className="container" style={{ maxWidth: '1100px' }}>
          <div style={{ textAlign: 'center', marginBottom: '3rem', maxWidth: '780px', margin: '0 auto 3rem' }}>
            <span className="caption" style={{
              color: 'var(--accent-warm)',
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              fontWeight: 600,
              fontSize: '0.85rem',
              display: 'inline-block',
              marginBottom: '1rem'
            }}>
              Identification
            </span>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Vous vous reconnaissez si…
            </h2>
            <p className="body-large" style={{ color: 'var(--text-muted)', lineHeight: 1.7 }}>
              Vous n'êtes pas seule. Ces ressentis du quotidien sont la raison pour laquelle de nombreuses femmes nous consultent — et trouvent enfin des réponses adaptées à leur corps.
            </p>
          </div>

          <div
            className="recognition-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '1.25rem',
              maxWidth: '1000px',
              margin: '0 auto'
            }}
          >
            {[
              "Vous mangez sainement mais votre poids ne bouge plus.",
              "Vous souffrez de fatigue chronique.",
              "Vous avez des fringales ou des compulsions alimentaires.",
              "Vous êtes en périménopause ou ménopause.",
              "Vous souffrez de SOPK ou de troubles hormonaux.",
              "Vous avez l'impression que votre corps a changé malgré vos efforts."
            ].map((text, idx) => (
              <div
                key={idx}
                className="recognition-card"
                data-testid={`recognition-item-${idx}`}
                style={{
                  display: 'flex',
                  gap: '0.85rem',
                  padding: '1.25rem 1.4rem',
                  background: 'var(--bg-page)',
                  border: '1px solid var(--border-light)',
                  borderRadius: '0.85rem',
                  alignItems: 'flex-start',
                  transition: 'all 0.3s ease'
                }}
              >
                <CheckCircle2 size={22} strokeWidth={2} style={{
                  color: 'var(--accent-primary)',
                  flexShrink: 0,
                  marginTop: '2px'
                }} />
                <p className="body-medium" style={{ margin: 0, lineHeight: 1.6, color: 'var(--text-primary)' }}>
                  {text}
                </p>
              </div>
            ))}
          </div>

          <div style={{ textAlign: 'center', marginTop: '3rem' }}>
            <p className="body-large" style={{
              fontStyle: 'italic',
              color: 'var(--accent-primary)',
              marginBottom: '1.5rem',
              fontWeight: 500
            }}>
              « Enfin quelqu'un qui comprend réellement ce que je vis. »
            </p>
            <a
              href={ONEDOC_ZARA}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
              style={{ textDecoration: 'none' }}
              data-testid="recognition-cta"
            >
              Réserver ma consultation
              <ArrowRight size={20} />
            </a>
          </div>

          <style>{`
            .recognition-card:hover {
              transform: translateY(-2px);
              border-color: var(--accent-primary);
              box-shadow: 0 6px 20px rgba(61, 84, 70, 0.10);
            }
            @media (max-width: 768px) {
              .recognition-grid {
                grid-template-columns: 1fr !important;
              }
            }
          `}</style>
        </div>
      </section>

      {/* Ce que mon accompagnement peut vous apporter */}
      <section
        data-testid="benefits-section"
        style={{
          position: 'relative',
          padding: '6rem 0',
          overflow: 'hidden',
          isolation: 'isolate'
        }}
      >
        {/* Image en arrière-plan */}
        <img
          src="/images/benefits-still.webp"
          alt="Ambiance sereine et premium — lin, branches d'eucalyptus et céramiques artisanales, symboles d'un accompagnement doux et durable"
          loading="lazy"
          decoding="async"
          className="benefits-bg-image"
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'left center',
            zIndex: -2
          }}
        />

        {/* Overlay clair pour lisibilité */}
        <div
          aria-hidden="true"
          className="benefits-overlay"
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(95deg, rgba(253,252,249,0.25) 0%, rgba(253,252,249,0.08) 30%, rgba(253,252,249,0.7) 55%, rgba(253,252,249,0.94) 85%)',
            zIndex: -1
          }}
        />

        <div className="container" style={{ maxWidth: '1200px', position: 'relative' }}>
          <div
            className="benefits-content"
            style={{
              maxWidth: '620px',
              marginLeft: 'auto'
            }}
          >
            <div style={{ marginBottom: '2rem' }}>
              <span className="caption" style={{
                color: 'var(--accent-warm)',
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                fontWeight: 700,
                fontSize: '0.85rem',
                display: 'inline-block',
                marginBottom: '1rem'
              }}>
                Les bénéfices concrets
              </span>
              <h2 className="heading-1" style={{ marginBottom: 0 }}>
                Ce que mon accompagnement peut vous apporter
              </h2>
            </div>

            <div
              className="benefits-grid"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '0.85rem',
                marginBottom: '2.5rem'
              }}
            >
              {[
                "Retrouver plus d'énergie",
                "Réduire les fringales",
                "Comprendre son fonctionnement hormonal",
                "Stabiliser ou perdre du poids durablement",
                "Retrouver une relation sereine avec l'alimentation",
                "Améliorer son bien-être au quotidien"
              ].map((text, idx) => (
                <div
                  key={idx}
                  data-testid={`benefit-item-${idx}`}
                  style={{
                    display: 'flex',
                    gap: '0.7rem',
                    padding: '0.9rem 1.1rem',
                    background: 'rgba(255, 255, 255, 0.92)',
                    backdropFilter: 'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)',
                    borderRadius: '0.75rem',
                    border: '1px solid rgba(255, 255, 255, 0.7)',
                    boxShadow: '0 6px 18px rgba(20, 45, 35, 0.08)',
                    alignItems: 'flex-start',
                    transition: 'all 0.3s ease'
                  }}
                  className="benefit-card"
                >
                  <CheckCircle2 size={20} strokeWidth={2.25} style={{
                    color: 'var(--accent-primary)',
                    flexShrink: 0,
                    marginTop: '2px'
                  }} />
                  <p className="body-medium" style={{ margin: 0, lineHeight: 1.5, fontWeight: 600, color: 'var(--text-primary)', fontSize: '0.92rem' }}>
                    {text}
                  </p>
                </div>
              ))}
            </div>

            <div>
              <a
                href={ONEDOC_ZARA}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
                style={{ textDecoration: 'none' }}
                data-testid="benefits-cta"
              >
                Réserver une consultation
                <ArrowRight size={20} />
              </a>
            </div>
          </div>

          <style>{`
            .benefit-card:hover {
              transform: translateY(-2px);
              border-color: var(--accent-primary);
              box-shadow: 0 10px 28px rgba(61, 84, 70, 0.18);
              background: rgba(255, 255, 255, 1) !important;
            }
            @media (max-width: 968px) {
              .benefits-content { max-width: 100% !important; margin: 0 !important; }
              .benefits-overlay {
                background: linear-gradient(180deg, rgba(253,252,249,0.15) 0%, rgba(253,252,249,0.6) 35%, rgba(253,252,249,0.94) 100%) !important;
              }
              .benefits-bg-image {
                object-position: center top !important;
              }
            }
            @media (max-width: 600px) {
              .benefits-grid { grid-template-columns: 1fr !important; }
            }
          `}</style>
        </div>
      </section>


      {/* Notre approche et nos prestations */}
      <section className="section-lg" style={{ background: 'var(--bg-page)', padding: '6rem 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', maxWidth: '780px', margin: '0 auto 4rem' }}>
            <span className="caption" style={{
              color: 'var(--accent-warm)',
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              fontWeight: 600,
              fontSize: '0.85rem',
              display: 'inline-block',
              marginBottom: '1rem'
            }}>
              Notre approche
            </span>
            <h2 className="heading-1" style={{ marginBottom: '1.25rem' }}>
              Deux axes pour votre équilibre
            </h2>
            <p className="body-large" style={{ color: 'var(--text-muted)', lineHeight: 1.7 }}>
              Une démarche sérieuse, humaine et experte — qui associe nutrition personnalisée et accompagnement centré sur les émotions.
            </p>
          </div>

          {/* Two Main Sections: Nutrition & Hypnopraxie */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '3rem',
            marginBottom: '3rem'
          }}
          className="dual-services-grid">
            {/* Section Nutrition */}
            <div style={{
              background: 'white',
              borderRadius: '1.5rem',
              overflow: 'hidden',
              boxShadow: '0 8px 30px rgba(0,0,0,0.08)',
              transition: 'all 0.3s ease',
              cursor: 'pointer'
            }}
            className="service-section-card">
              <div style={{
                height: '350px',
                overflow: 'hidden',
                position: 'relative'
              }}>
                <img 
                  src="/images/nutrition-card.webp"
                  alt="Table de consultation nutritionnelle premium — légumes frais, légumineuses et carnet de suivi, Nutri Centre Riviera Vevey-Montreux"
                  loading="lazy"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    transition: 'transform 0.3s ease'
                  }}
                  className="service-section-image"
          decoding="async"/>
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.3) 100%)'
                }} />
              </div>
              <div style={{ padding: '2.5rem' }}>
                <h3 className="heading-2" style={{ marginBottom: '1rem', color: 'var(--text-primary)' }}>
                  Nutrition
                </h3>
                <p className="body-medium" style={{ 
                  marginBottom: '1.5rem', 
                  color: 'var(--accent-primary)',
                  fontWeight: 500
                }}>
                  Une approche scientifique et personnalisée de votre santé
                </p>
                <p className="body-medium" style={{ marginBottom: '2rem', color: 'var(--text-muted)', lineHeight: '1.6' }}>
                  Une prise en charge nutritionnelle globale : bilans métaboliques, équilibre hormonal, perte de poids durable et accompagnement adapté à chaque femme.
                </p>
                <Link 
                  to="/nutrition-hormonale" 
                  className="btn-primary"
                  style={{ textDecoration: 'none', display: 'inline-flex' }}
                  data-testid="home-nutrition-cta"
                >
                  En savoir plus
                  <ArrowRight size={20} />
                </Link>
              </div>
            </div>

            {/* Section Hypnopraxie */}
            <div style={{
              background: 'white',
              borderRadius: '1.5rem',
              overflow: 'hidden',
              boxShadow: '0 8px 30px rgba(0,0,0,0.08)',
              transition: 'all 0.3s ease',
              cursor: 'pointer'
            }}
            className="service-section-card">
              <div style={{
                height: '350px',
                overflow: 'hidden',
                position: 'relative'
              }}>
                <img 
                  src="/images/hypnopraxie-card.webp"
                  alt="Salle d'hypnopraxie apaisante aux tons sauge et crème — fauteuil confortable et lumière douce, Nutri Centre Riviera Vevey-Montreux"
                  loading="lazy"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    transition: 'transform 0.3s ease'
                  }}
                  className="service-section-image"
          decoding="async"/>
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.3) 100%)'
                }} />
              </div>
              <div style={{ padding: '2.5rem' }}>
                <h3 className="heading-2" style={{ marginBottom: '1rem', color: 'var(--text-primary)', fontSize: '1.5rem', lineHeight: 1.3 }}>
                  Accompagnement thérapeutique centré sur les émotions et le fonctionnement intérieur <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(Hypnopraxie)</span>
                </h3>
                <p className="body-medium" style={{ 
                  marginBottom: '1.5rem', 
                  color: 'var(--accent-primary)',
                  fontWeight: 500
                }}>
                  Une méthode douce pour apaiser, comprendre et libérer
                </p>
                <p className="body-medium" style={{ marginBottom: '2rem', color: 'var(--text-muted)', lineHeight: '1.6' }}>
                  Une méthode avancée de l'hypnose qui accède à vos ressentis profonds — les <em>éprouvés</em> — pour apaiser, comprendre et favoriser un changement durable.
                </p>
                <Link 
                  to="/hypnopraxie-vevey-montreux" 
                  className="btn-primary"
                  style={{ textDecoration: 'none', display: 'inline-flex' }}
                  data-testid="home-hypnopraxie-cta"
                >
                  En savoir plus
                  <ArrowRight size={20} />
                </Link>
              </div>
            </div>
          </div>

          {/* Responsive + Hover Effects */}
          <style>{`
            @media (max-width: 968px) {
              .dual-services-grid {
                grid-template-columns: 1fr !important;
                gap: 2rem !important;
              }
            }
            .service-section-card:hover {
              transform: translateY(-8px);
              box-shadow: 0 12px 40px rgba(0,0,0,0.12);
            }
            .service-section-card:hover .service-section-image {
              transform: scale(1.05);
            }
          `}</style>
        </div>
      </section>

      {/* Remboursement des consultations */}
      <section
        data-testid="remboursement-section"
        style={{
          background: 'linear-gradient(135deg, var(--bg-page) 0%, white 100%)',
          padding: '6rem 0',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Decorative circle */}
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: '-200px',
            right: '-200px',
            width: '500px',
            height: '500px',
            borderRadius: '50%',
            background: 'var(--accent-primary-light, #E9EEE8)',
            opacity: 0.5,
            filter: 'blur(60px)',
            pointerEvents: 'none'
          }}
        />

        <div className="container" style={{ maxWidth: '780px', position: 'relative', textAlign: 'center' }}>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.55rem',
              padding: '0.55rem 1.15rem',
              borderRadius: '999px',
              background: 'white',
              border: '1px solid var(--accent-primary)',
              color: 'var(--accent-primary)',
              fontSize: '0.78rem',
              fontWeight: 700,
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              marginBottom: '1.25rem',
              boxShadow: '0 4px 14px rgba(61, 84, 70, 0.10)'
            }}
          >
            <ShieldCheck size={16} strokeWidth={2.25} />
            Agréments officiels
          </div>
          <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
            Remboursement des consultations
          </h2>
          <p
            className="body-large"
            style={{
              color: 'var(--text-muted)',
              maxWidth: '640px',
              margin: '0 auto',
              lineHeight: 1.7
            }}
          >
            Le centre est agréé <strong>ASCA</strong> et <strong>RME</strong> — vos consultations peuvent être prises en charge par votre assurance complémentaire.
          </p>
        </div>
      </section>

      {/* Collaborations */}
      <section
        data-testid="partners-section"
        style={{ background: 'var(--bg-section)', padding: '3rem 0', overflow: 'hidden' }}
      >
        <div
          className="partners-marquee"
          data-testid="partners-marquee"
          style={{
            display: 'flex',
            overflow: 'hidden',
            maskImage:
              'linear-gradient(90deg, transparent 0, black 8%, black 92%, transparent 100%)',
            WebkitMaskImage:
              'linear-gradient(90deg, transparent 0, black 8%, black 92%, transparent 100%)'
          }}
        >
          <div
            className="partners-track"
            style={{
              display: 'flex',
              gap: '3.5rem',
              alignItems: 'center',
              paddingLeft: '3.5rem',
              animation: 'partners-scroll 28s linear infinite',
              whiteSpace: 'nowrap'
            }}
          >
            {[...Array(2)].map((_, dup) => (
              <React.Fragment key={dup}>
                {[
                  {
                    name: 'ASCA',
                    url: 'https://asca.ch/',
                    logo: '/images/home-section-3.webp',
                    testId: 'partner-asca'
                  },
                  {
                    name: 'RME',
                    url: 'https://emr.ch/fr/accueil',
                    logo: '/images/home-section-1.webp',
                    testId: 'partner-rme'
                  },
                  {
                    name: 'École TCMA Genève',
                    url: 'https://asca.ch/',
                    logo: '/images/home-section-4.webp',
                    testId: 'partner-tcma'
                  },
                  {
                    name: 'HYPSOS Institut',
                    url: 'https://www.hypnose-suisse-formation.ch/',
                    logo: '/images/home-section-2.webp',
                    testId: 'partner-hypsos'
                  }
                ].map((p, i) => (
                  <a
                    key={`${dup}-${i}`}
                    href={p.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={dup === 0 ? p.testId : undefined}
                    aria-label={p.name}
                    aria-hidden={dup === 1}
                    className="partner-logo"
                    style={{
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '180px',
                      height: '120px',
                      padding: '0.75rem',
                      transition: 'transform 0.3s ease, filter 0.3s ease',
                      cursor: 'pointer'
                    }}
                  >
                    <img
                      src={p.logo}
                      alt={p.name}
                      loading="lazy"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        width: 'auto',
                        height: 'auto',
                        objectFit: 'contain'
                      }}
          decoding="async"/>
                  </a>
                ))}
              </React.Fragment>
            ))}
          </div>
        </div>

        <style>{`
          @keyframes partners-scroll {
            from { transform: translateX(0); }
            to { transform: translateX(-50%); }
          }
          .partner-logo:hover {
            transform: translateY(-3px);
          }
          .partners-marquee:hover .partners-track {
            animation-play-state: paused;
          }
        `}</style>
      </section>

      {/* CTA Section */}
      <section style={{
        background: 'var(--accent-primary)',
        color: 'white',
        padding: '5rem 0'
      }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <h2 className="heading-1" style={{ color: 'white', marginBottom: '1rem' }}>
            {siteData.appointmentSection.title}
          </h2>
          <p className="body-large" style={{ 
            color: 'white', 
            opacity: 0.95, 
            maxWidth: '600px', 
            margin: '0 auto 2.5rem' 
          }}>
            {siteData.appointmentSection.description}
          </p>
          <a 
            href={ONEDOC_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary" 
            style={{ 
              background: 'white', 
              color: 'var(--accent-primary)',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
              textDecoration: 'none'
            }}
          >
            {siteData.cta.primary}
            <ArrowRight size={20} />
          </a>
          <p className="caption" style={{ 
            marginTop: '1rem', 
            color: 'white',
            opacity: 0.9,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem'
          }}>
            <CheckCircle2 size={16} />
            {siteData.cta.subtext}
          </p>
        </div>
      </section>

      <FAQ items={homeFaq} />

      <RelatedPages items={relatedPagesMap['home']} heading="Découvrir nos thématiques de soin" />
    </div>
    </>
  );
};

export default Home;
